import React, { useMemo, useRef, useState } from 'react';
import { Button, Tooltip } from '@usealloy/component-library';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import cloneDeepWith from 'lodash/cloneDeepWith';
import moment from 'moment';

import checkPermissions from '~/evaluations/helpers/permissions';
import Collapsibox from '~/components/Collapsibox';
import { snakeToCaps, isLink, getValidLink } from '~/helpers/formatting';
import { usePermissions } from '~/helpers/hooks';

import MetadataTemplateModal from './MetadataTemplateModal';

import { LineBox, MetaValueText, MetaKeyText } from './MetadataTemplateModal/styles';
import { DecisionTypesMap } from '@usealloy/typegen';

const MetaDataPropTypes = {
  meta: PropTypes.object.isRequired,
  finalResponse: PropTypes.object.isRequired,
  template: PropTypes.object,
  evaluationApplicationId: PropTypes.number.isRequired,
  isJourneyApplicationPage: PropTypes.bool,
  decisionType: PropTypes.string,
};

const MetaDataDefaultProps = {
  template: null,
  isJourneyApplicationPage: false,
  decisionType: null,
};

const MetaData = ({
  meta,
  finalResponse,
  template,
  evaluationApplicationId,
  isJourneyApplicationPage,
  decisionType,
}) => {
  const response = useRef(cloneDeepWith(finalResponse));
  const metadata = useRef(cloneDeepWith(meta));
  const [permissions] = usePermissions();
  const canSeePanel = checkPermissions(permissions, ['dob', 'ssn']);
  const initialWhitelist = useMemo(() => (
    template
      ? cloneDeepWith(template.whitelist)
      : cloneDeepWith({ meta: metadata.current }, val => (val instanceof Object ? undefined : true))
  ), [template]);

  const [metadataTemplateModalIsOpen, setMetadataTemplateModalIsOpen] = useState(false);
  const [state, setState] = useState({
    // This is either null or an object representing the currently saved template
    // that is filtering the other data section
    template,
    // This represents the actual whitelist object that we're comparing against the final response
    whitelist: initialWhitelist,
  });

  const templateCreatedAt = state.template
    ? moment.utc(state.template.created_at).local().format('MMM D YYYY h:mm A')
    : null;


  // Recursively render the metadata object
  let top = true;
  const renderRecursiveWhitelist = (whitelist = {}, parentSelector = []) => {
    const filteredObj = state.template === null
      ? { meta: metadata.current } : response.current;
    const obj = parentSelector.length ? get(filteredObj, parentSelector) : filteredObj;
    let objKeys = Object.keys(obj);
    const whitelistKeys = whitelist ? Object.keys(whitelist) : [];
    objKeys = objKeys.filter(key => {
      const whitelistValue = whitelist?.[key];
      return (
        (whitelist === null || whitelistKeys.includes(key))
        && (
          (whitelist === null) // keep nulls
          || (whitelistValue === 'top') // "top" is a special case for hiding parent keys
          || (whitelistValue && typeof whitelistValue === 'boolean') // renders { key: true }
          || (whitelistValue && typeof whitelistValue === 'object' && Object.keys(whitelistValue).length) // renders objects with children, but not empty objects
        )
      );
    });

    const mappedKeys = objKeys.map(key => {
      const value = obj[key];
      // Construct the full key to the current value
      const fullKey = parentSelector.length ? [...parentSelector, key] : [key];
      const fullKeyStr = fullKey.join('.');
      const level = fullKey.length - 1;

      // The key we're trying to render is an object, so we need to recurse down
      // and render it's children
      const whitelistValue = whitelist === null ? whitelist : whitelist[key];

      // Pass whitelist as null to next level down if the current whitelist object
      // key is true. Means render any and all descendents of this value
      const list = [true, 'top'].includes(whitelistValue)
        ? null
        : whitelistValue;

      // Determine if the value is one we can recurse into
      let shouldRecurse = value instanceof Object
        && (
          !Array.isArray(value)
          || (
            value.length
            && value[0] instanceof Object
            && !Array.isArray(value[0])
          )
        );

      // determine value to display
      let valueToDisplay = String(value);
      if (Array.isArray(value)) {
        shouldRecurse = true;
      } else if (isLink(value)) {
        const { url, display } = getValidLink(value);
        // target="_blank" implies rel="noopener" behavior
        // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#browser_compatibility
        // eslint-disable-next-line react/jsx-no-target-blank
        valueToDisplay = <a href={url} target="_blank">{display}</a>;
      }

      if (shouldRecurse) {
        const newLine = Object.keys(value).length !== 0;
        const render = newLine && renderRecursiveWhitelist(list, fullKey);
        if (render && top) return render;

        const recursiveRender = (
          <div key={fullKeyStr}>
            <LineBox
              justifyContent="flex-start"
              alignItems="flex-start"
              direction="row"
            >
              <MetaKeyText lineHeight={5} size={3} weight={700}>{`${snakeToCaps(key)}:`}</MetaKeyText>
              {!newLine && <MetaValueText lineHeight={5} size={8}>{valueToDisplay}</MetaValueText>}
            </LineBox>
            {render}
          </div>
        );
        if (whitelistValue === 'top' && level > 0) top = true;
        return recursiveRender;
      }
      if (whitelistValue === 'top' && level > 0) top = true;
      else top = false;

      return (
        <div key={fullKeyStr}>
          <LineBox
            key={fullKeyStr}
            justifyContent="flex-start"
            alignItems="flex-start"
            direction="row"
          >
            <MetaKeyText lineHeight={5} size={3} weight={700}>{`${snakeToCaps(key)}:`}</MetaKeyText>
            <MetaValueText lineHeight={5} size={3}>{valueToDisplay}</MetaValueText>
          </LineBox>
        </div>
      );
    });

    if (objKeys.length > 0) {
      return top ? mappedKeys : <div className="row-space-left">{mappedKeys}</div>;
    }

    return false;
  };


  const renderEditButton = () => {
    if (
      !permissions['CREATE:EvaluationMetadataTemplates:null']
      || (isJourneyApplicationPage && !permissions['UPDATE:JourneyApplications:journey_application_id'])
      || decisionType === DecisionTypesMap.INFORMATIONAL
    ) return [];

    return (
      <Tooltip content="Edit the viewable metadata displayed for Evaluations">
        <Button
          icon="Edit"
          onClick={() => { setMetadataTemplateModalIsOpen(true); }}
          tabIndex={0}
          variant="tertiary"
        />
      </Tooltip>
    );
  };

  const showWhitelist = meta !== undefined
    || state.template !== null;

  return canSeePanel && (
    <>
      <MetadataTemplateModal
        closeModal={() => { setMetadataTemplateModalIsOpen(false); }}
        data={finalResponse || {}}
        evaluationApplicationId={evaluationApplicationId}
        isOpen={metadataTemplateModalIsOpen}
        initialWhitelist={initialWhitelist || {}}
        setState={setState}
      />
      <Collapsibox
        header="Other Data"
        headerButtons={[state.template && (
          <span key={-1}>
            <Tooltip
              content={`Saved ${templateCreatedAt} by ${state.template.email}`}
            >
              <span className="grey">
                Filtered
              </span>
            </Tooltip>
          </span>
        )].concat(renderEditButton())}
      >
        <div className="metadata pad-16-24">
          {showWhitelist && renderRecursiveWhitelist(state.whitelist)}
        </div>
      </Collapsibox>
    </>
  );
};
MetaData.propTypes = MetaDataPropTypes;
MetaData.defaultProps = MetaDataDefaultProps;

export default MetaData;
